import { zodResolver } from '@hookform/resolvers/zod';
import { useForm, useWatch } from 'react-hook-form';

import { DateRangePicker } from '@/components/common/date-picker';
import { Drawer } from '@/components/feedback/drawer';
import { MultiSelectField } from '@/components/forms';
import { appTzLabel, fromDateRange, toDayjsRange } from '@/lib/date';

import { toDefaults, toFilters } from '../filters';
import { APPLICABLE_TO_LABEL, DISCOUNT_FORMAT_LABEL, DISCOUNT_STATUS_CHIP } from '../format';
import { discountFiltersSchema } from '../schemas';
import type { DiscountFilterFormValues, DiscountListFilters } from '../types';

const FORMAT_OPTIONS = (
  Object.keys(DISCOUNT_FORMAT_LABEL) as (keyof typeof DISCOUNT_FORMAT_LABEL)[]
).map((value) => ({ value, label: DISCOUNT_FORMAT_LABEL[value] }));
const APPLICABLE_OPTIONS = (
  Object.keys(APPLICABLE_TO_LABEL) as (keyof typeof APPLICABLE_TO_LABEL)[]
).map((value) => ({ value, label: APPLICABLE_TO_LABEL[value] }));
const STATUS_OPTIONS = (
  Object.keys(DISCOUNT_STATUS_CHIP) as (keyof typeof DISCOUNT_STATUS_CHIP)[]
).map((value) => ({ value, label: DISCOUNT_STATUS_CHIP[value].label }));

export type DiscountFilterDrawerProps = {
  open: boolean;
  /** Last applied filters; the draft re-seeds from this on each open. */
  value: DiscountListFilters;
  onApply: (filters: DiscountListFilters) => void;
  onClose: () => void;
};

type BodyProps = Omit<DiscountFilterDrawerProps, 'open'>;

function FilterBody({ value, onApply, onClose }: BodyProps) {
  const draft = toDefaults(value);
  const form = useForm<DiscountFilterFormValues>({
    resolver: zodResolver(discountFiltersSchema),
    // `toDefaults` yields `undefined` for the enum date bounds, but the schema requires nullable;
    // coerce to null so an untouched form validates (mirrors PaymentFilterDrawer).
    defaultValues: {
      ...draft,
      createdFrom: draft.createdFrom ?? null,
      createdTo: draft.createdTo ?? null,
    },
    mode: 'onChange',
  });

  const [discountFormat, applicableTo, status, createdFrom, createdTo] = useWatch({
    control: form.control,
    name: ['discountFormat', 'applicableTo', 'status', 'createdFrom', 'createdTo'],
  });
  const hasCriterion =
    Boolean(discountFormat?.length) ||
    Boolean(applicableTo?.length) ||
    Boolean(status?.length) ||
    Boolean(createdFrom) ||
    Boolean(createdTo);

  const dateRange = createdFrom && createdTo ? toDayjsRange([createdFrom, createdTo]) : null;

  const handleApply = form.handleSubmit((values) => {
    onApply(toFilters(values));
    onClose();
  });

  return (
    <Drawer
      open
      onClose={onClose}
      title="Filter"
      width={400}
      primaryText="Apply"
      onPrimary={handleApply}
      primaryDisabled={!hasCriterion}
      cancelText="Cancel"
      onCancel={onClose}
    >
      <MultiSelectField
        name="discountFormat"
        control={form.control}
        label="Discount Format"
        placeholder="Select"
        options={FORMAT_OPTIONS}
      />
      <MultiSelectField
        name="applicableTo"
        control={form.control}
        label="Applicable To"
        placeholder="Select"
        options={APPLICABLE_OPTIONS}
      />
      {/* Figma 652-242945 field order: Format → Applicable To → Created Date Range → Status. */}
      <fieldset style={{ border: 0, padding: 0, margin: 0 }}>
        <legend>{appTzLabel('Created Date Range')}</legend>
        <DateRangePicker
          aria-label={appTzLabel('Created Date Range')}
          format="MM/DD/YYYY"
          value={dateRange}
          onChange={(range) => {
            const iso = fromDateRange(range);
            form.setValue('createdFrom', iso?.[0] ?? null, { shouldValidate: true });
            form.setValue('createdTo', iso?.[1] ?? null, { shouldValidate: true });
          }}
          style={{ width: '100%' }}
        />
      </fieldset>
      <MultiSelectField
        name="status"
        control={form.control}
        label="Status"
        placeholder="Select"
        options={STATUS_OPTIONS}
      />
    </Drawer>
  );
}

export function DiscountFilterDrawer({ open, ...rest }: DiscountFilterDrawerProps) {
  if (!open) {
    return (
      <Drawer open={false} onClose={rest.onClose}>
        {null}
      </Drawer>
    );
  }
  // Unmount on close; remounts fresh on open so the draft re-seeds from the last applied `value`.
  return <FilterBody {...rest} />;
}
